import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import { Container as ContainerBase } from 'components/misc/Layouts';
import tw from 'twin.macro';
import styled from 'styled-components';
import illustration from 'images/login-illustration.svg';
import logo from 'images/logo.svg';
import { resetearPassword, validarResetToken } from 'scripts/resetPasswordScript';

const Container = tw(ContainerBase)`min-h-screen bg-[#99cdb6] text-white font-medium flex justify-center`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-1/2 xl:w-5/12 p-6 sm:p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`w-40 h-40 mx-auto rounded-full`;
const MainContent = tw.div`mt-12 flex flex-col items-center`;
const Heading = tw.h1`text-2xl xl:text-3xl font-extrabold`;
const FormContainer = tw.div`w-full flex-1 mt-8`;

const Form = tw.form`mx-auto max-w-xs`;
const Input = tw.input`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0`;
const SubmitButton = styled.button`
	${tw`mt-5 tracking-wide font-semibold bg-primary-500 text-gray-100 w-full py-4 rounded-lg hover:bg-primary-900 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none`}
	.text {
		${tw`ml-3`}
	}
`;
const IllustrationContainer = tw.div`sm:rounded-r-lg flex-1 bg-purple-100 text-center hidden lg:flex justify-center`;
const IllustrationImage = styled.div`
	${(props) => `background-image: url("${props.imageSrc}");`}
	${tw`m-12 xl:m-16 w-full max-w-lg bg-contain bg-center bg-no-repeat`}
`;

export default ({
	logoLinkUrl = '/',
	illustrationImageSrc = illustration,
	headingText = 'Restablecer Contraseña',
	submitButtonText = 'Guardar Nueva Contraseña',
	loginUrl = 'login',
}) => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get('token');

	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState('');
	const [validandoToken, setValidandoToken] = useState(true);
	const [tokenValido, setTokenValido] = useState(false);

	useEffect(() => {
		let mounted = true;

		const validarToken = async () => {
			if (!token) {
				if (mounted) {
					setError('Enlace inválido. Solicita un nuevo correo de recuperación.');
					setTokenValido(false);
					setValidandoToken(false);
				}
				return;
			}

			try {
				await validarResetToken(token);
				if (mounted) {
					setTokenValido(true);
					setError('');
				}
			} catch (validationError) {
				if (mounted) {
					setTokenValido(false);
					setError(validationError?.message || 'Token inválido o expirado.');
				}
			} finally {
				if (mounted) {
					setValidandoToken(false);
				}
			}
		};

		validarToken();

		return () => {
			mounted = false;
		};
	}, [token]);

	const handleResetPassword = async (event) => {
		event.preventDefault();

		if (!tokenValido) {
			setError('Enlace inválido o expirado. Solicita uno nuevo.');
			return;
		}

		// Validar que las contraseñas coincidan
		if (password !== confirmPassword) {
			setError('Las contraseñas no coinciden.');
			return;
		}
		resetearPassword(token, password)
			.then((data) => {
				toast.success(data.message);
				setError('');
				setPassword('');
				setConfirmPassword('');
				setTimeout(() => {
					window.location.href = '/login';
				}, 3000);
			})
			.catch((error) => {
				setError(error.message);
			});
	};

	return (
		<Container>
			<Content>
				<MainContainer>
					<LogoLink href={logoLinkUrl}>
						<LogoImage src={logo} />
					</LogoLink>
					<MainContent>
						<Heading>{headingText}</Heading>
						<FormContainer>
							{validandoToken ? (
								<p tw="text-center text-gray-600">Validando enlace de recuperación...</p>
							) : tokenValido ? (
								<Form onSubmit={handleResetPassword}>
									<Input
										type="password"
										placeholder="Nueva contraseña"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
									/>
									<Input
										type="password"
										placeholder="Confirmar nueva contraseña"
										value={confirmPassword}
										onChange={(e) => setConfirmPassword(e.target.value)}
										required
									/>
									<SubmitButton type="submit">
										<span className="text">{submitButtonText}</span>
									</SubmitButton>
									{error && (
										<p style={{ color: 'red' }} tw="mt-5 text-center">
											{error}
										</p>
									)}
									<p tw="mt-6 text-xs text-gray-600 text-center">
										¿Ya tienes cuenta?{' '}
										<a href={loginUrl} tw="border-b border-gray-500 border-dotted">
											Iniciar sesión
										</a>
									</p>
								</Form>
							) : (
								<div tw="text-center">
									<p style={{ color: 'red' }}>{error || 'El enlace es inválido o expiró.'}</p>
									<p tw="mt-4 text-xs text-gray-600">
										Solicita un nuevo enlace en{' '}
										<a href="/forgot-password" tw="border-b border-gray-500 border-dotted">
											recuperar contraseña
										</a>
										.
									</p>
								</div>
							)}
						</FormContainer>
					</MainContent>
				</MainContainer>
				<IllustrationContainer>
					<IllustrationImage imageSrc={illustrationImageSrc} />
				</IllustrationContainer>
			</Content>
		</Container>
	);
};
